/**
 * Utility functions for handling message actions and formatting
 */

import { normalizeAiMarkdown } from './aiMarkdown';

export class MessageActionHandler {
  /**
   * Process a message chunk and apply any actions (like navigation)
   * @param {string} messageText - The original message text
   * @param {Object} action - The action object from the chunk
   * @returns {string} - The formatted message text with actions applied
   */
  static processMessageAction(messageText, action) {
    const normalizedMessageText = normalizeAiMarkdown(messageText);

    if (!action) {
      return normalizedMessageText;
    }

    switch (action.type?.toUpperCase()) {
      case 'NAVIGATE':
        return this.handleNavigationAction(normalizedMessageText, action);
      case 'REDIRECT':
        return this.handleRedirectAction(normalizedMessageText, action);
      case 'SCHEDULE':
        return this.handleScheduleAction(normalizedMessageText, action);
      case 'UPLOAD':
        return this.handleUploadAction(normalizedMessageText, action);
      case 'NEGOTIATE':
        return this.handleNegotiateAction(normalizedMessageText, action);
      // Add more action types as needed
      default:
        return normalizedMessageText;
    }
  }

  /**
   * Handle navigation action by adding a clickable link
   * @param {string} messageText - The original message text
   * @param {Object} action - The navigation action object
   * @returns {string} - Message text with navigation link
   */
  static handleNavigationAction(messageText, action) {
    const baseUrl = action.target;
    const params = action.params;

    if (!baseUrl) {
      return messageText;
    }

    const fullUrl = params ? `${baseUrl}?${params}` : baseUrl;
    const buttonConfig = this.getActionButtonConfig(action);
    // const buttonText = buttonConfig ? `${buttonConfig.icon} ${buttonConfig.text}` : '🔗 Open';
    const buttonText = buttonConfig ? `${buttonConfig.text}` : '🔗 Open';

    return `${messageText}\n\n[${buttonText}](${fullUrl})`;
  }

  /**
   * Handle redirect action (similar to navigation but could have different styling)
   * @param {string} messageText - The original message text
   * @param {Object} action - The redirect action object
   * @returns {string} - Message text with redirect link
   */
  static handleRedirectAction(messageText, action) {
    const baseUrl = action.target;
    const params = action.params;
    
    if (!baseUrl) {
      return messageText;
    }

    const fullUrl = params ? `${baseUrl}?${params}` : baseUrl;
    const buttonText = action.buttonText || 'Go to Page';
    
    return `${messageText}\n\n🔗 [${buttonText}](${fullUrl})`;
  }

  /**
   * Handle schedule action (e.g., booking a consultation)
   * @param {string} messageText - The original message text
   * @param {Object} action - The schedule action object
   * @returns {string} - Message text with schedule action
   */
  static handleScheduleAction(messageText, action) {
    const buttonText = action.buttonText || 'Book Consultation';
    const url = action.target || '/schedule';
    const params = action.params;
    
    const fullUrl = params ? `${url}?${params}` : url;
    
    return `${messageText}\n\n📅 [${buttonText}](${fullUrl})`;
  }

  /**
   * Handle upload action (e.g., uploading documents)
   * @param {string} messageText - The original message text
   * @param {Object} action - The upload action object
   * @returns {string} - Message text with upload action
   */
  static handleUploadAction(messageText, action) {
    const buttonText = action.buttonText || 'Upload Documents';
    const url = action.target || '/upload';
    const params = action.params;
    
    const fullUrl = params ? `${url}?${params}` : url;
    
    return `${messageText}\n\n📎 [${buttonText}](${fullUrl})`;
  }

  /**
   * Handle negotiate action (e.g., starting negotiation)
   * @param {string} messageText - The original message text
   * @param {Object} action - The negotiate action object
   * @returns {string} - Message text with negotiate action
   */
  static handleNegotiateAction(messageText, action) {
    const buttonText = action.buttonText || 'Start Negotiation';
    const url = action.target || '/negotiate';
    const params = action.params;
    
    const fullUrl = params ? `${url}?${params}` : url;
    
    return `${messageText}\n\n💬 [${buttonText}](${fullUrl})`;
  }

  /**
   * Check if a chunk has a valid action
   * @param {Object} chunk - The message chunk
   * @returns {boolean} - True if chunk has a valid action
   */
  static hasAction(chunk) {
    return chunk?.action && chunk.action.type;
  }

  /**
   * Get action type from chunk
   * @param {Object} chunk - The message chunk
   * @returns {string|null} - The action type or null if no action
   */
  static getActionType(chunk) {
    return chunk?.action?.type?.toUpperCase() || null;
  }

  /**
   * Build URL with parameters
   * @param {string} baseUrl - The base URL
   * @param {string|Object} params - URL parameters as string or object
   * @returns {string} - Complete URL with parameters
   */
  static buildUrl(baseUrl, params) {
    if (!params) {
      return baseUrl;
    }

    if (typeof params === 'string') {
      return `${baseUrl}?${params}`;
    }

    if (typeof params === 'object') {
      const urlParams = new URLSearchParams(params);
      return `${baseUrl}?${urlParams.toString()}`;
    }

    return baseUrl;
  }

  /**
   * Check if a message has an action
   * @param {Object} message - The message object
   * @returns {boolean} - Whether the message has an action
   */
  static hasAction(message) {
    return message && message.action && message.action.type;
  }

  /**
   * Get action type from a message
   * @param {Object} message - The message object
   * @returns {string|null} - The action type or null
   */
  static getActionType(message) {
    return this.hasAction(message) ? message.action.type.toUpperCase() : null;
  }

  /**
   * Build URL from action parameters
   * @param {Object} action - The action object
   * @returns {string} - The complete URL
   */
  static buildUrl(action) {
    if (!action || !action.target) return '';
    
    const baseUrl = action.target;
    const params = action.params;
    
    return params ? `${baseUrl}?${params}` : baseUrl;
  }

  /**
   * Get action button configuration
   * @param {Object} action - The action object
   * @returns {Object} - Button configuration object
   */
  static getActionButtonConfig(action) {
    if (!action) return null;

    const configs = {
      NAVIGATE: {
        icon: '🔗',
        color: 'primary',
        variant: 'contained'
      },
      SCHEDULE: {
        icon: '📅',
        color: 'secondary',
        variant: 'contained'
      },
      UPLOAD: {
        icon: '📎',
        color: 'info',
        variant: 'outlined'
      },
      NEGOTIATE: {
        icon: '💬',
        color: 'warning',
        variant: 'contained'
      },
      REDIRECT: {
        icon: '🔗',
        color: 'primary',
        variant: 'outlined'
      }
    };

    const actionType = action.type?.toUpperCase();
    const config = configs[actionType] || configs.NAVIGATE;
    
    return {
      ...config,
      text: action.buttonText || this.getDefaultButtonText(actionType),
      url: this.buildUrl(action)
    };
  }

  /**
   * Get default button text for action type
   * @param {string} actionType - The action type
   * @returns {string} - Default button text
   */
  static getDefaultButtonText(actionType) {
    const defaults = {
      NAVIGATE: 'Go To',
      SCHEDULE: 'Book Consultation',
      UPLOAD: 'Upload Documents',
      NEGOTIATE: 'Start Negotiation',
      REDIRECT: 'Go to Page'
    };

    return defaults[actionType] || 'Take Action';
  }
}

// Export individual functions for convenience
export const processMessageAction = MessageActionHandler.processMessageAction.bind(MessageActionHandler);
export const handleNavigationAction = MessageActionHandler.handleNavigationAction.bind(MessageActionHandler);
export const hasAction = MessageActionHandler.hasAction.bind(MessageActionHandler);
export const getActionType = MessageActionHandler.getActionType.bind(MessageActionHandler);
export const buildUrl = MessageActionHandler.buildUrl.bind(MessageActionHandler);

export default MessageActionHandler;